/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable  react-hooks/exhaustive-deps */
import { ColumnsType } from 'antd/lib/table';
import produce from 'immer';
import { isEmpty } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseMutationResult } from 'react-query';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import { DeleteButton, EditButton, Space, Table } from '~/components';
import theme from '~/constants/theme';
import { useGetContactsApi, useMutateContactsApi } from '~/controllers/contacts';
import { useGetApplication } from '~/controllers/wizard';
import { CheckThinIcon } from '~/img';
import { Draft } from '~/types/Draft';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { Shareholders } from '~/types/Shareholders';
import { contactObjCreation, getUpdatedContact, getUpdatedShareholderDataSync } from '~/utils/contacts';
import { getTableRowsErros } from '~/utils/getTableRowsErros';
import { hasSoftDeclineFields } from '~/utils/softDeclinesFields';

import IndividualAdd from './IndividualAdd';
import IndividualCardMobile from './IndividualCardMobile';
import IndividualEdit from './IndividualEdit';
import { isShareholderEditButtonDisabled } from './utils';

const IndividualTable: React.FC<Partial<FormItemInjectedProps<Shareholders>>> = (props) => {
  const { value, onChange } = props as FormItemInjectedProps<Shareholders>;
  const shareholdersFormState = { shareholders: value };
  const allContacts: any = useGetContactsApi();
  const { refetch: refetchContacts } = allContacts;
  const { t } = useTranslation('fillApplication');
  const applicationQuery = useGetApplication();
  const hasIndividualsValidationWarnings = hasSoftDeclineFields(applicationQuery.data, 'shareholders.individuals[]');
  const globeState = useSelector((state: any) => state?.state);
  const { commonFormErrors } = globeState;
  const tableRowErrors = getTableRowsErros({
    commonFormErrors: commonFormErrors['/wizard/application/shareholders'],
    tableKey: 'individuals',
  });
  const { errorRowsKeys } = tableRowErrors;
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.sm });
  const [editId, setId] = useState('');
  const [sourceOfEditing, setSourceOfEditing] = useState('users');
  const openEdit = (uid: string) => {
    setId(uid);
  };
  const closeEdit = () => {
    setId('');
  };

  const contactMutation = useMutateContactsApi();

  const individualColumns: ColumnsType<IndividualRowType> = useMemo(
    () => [
      {
        title: t('First name'),
        dataIndex: 'firstName',
        key: 'firstName',
      },
      {
        title: t('Last name'),
        dataIndex: 'lastName',
        key: 'lastName',
      },
      {
        title: t('Passport'),
        dataIndex: 'hasPassport',
        key: 'hasPassport',
        render: (hasPassport: boolean) => {
          if (hasPassport) return <CheckThinIcon />;
          return null;
        },
      },
      {
        title: t('Emirates ID'),
        dataIndex: 'hasEmiratesId',
        key: 'hasEmiratesId',
        render: (hasEmiratesId: boolean) => {
          if (hasEmiratesId) return <CheckThinIcon />;
          return null;
        },
      },
      {
        title: t('Shares %'),
        dataIndex: 'shares',
        key: 'shares',
      },
      {
        title: '',
        key: 'action',
        fixed: 'right',
        render: (_, { onEdit, onDelete }, index) => (
          <Space size={16} style={actionsStyle}>
            <EditButton
              id="individualTable_EditButton"
              disabled={isShareholderEditButtonDisabled(applicationQuery.data, hasIndividualsValidationWarnings)}
              hasValidationWarnings={hasIndividualsValidationWarnings}
              onClick={onEdit}
              hasError={errorRowsKeys?.includes(`${index}`)}
            />
            <DeleteButton
              id="individualTable_DeleteButton"
              disabled={isShareholderEditButtonDisabled(applicationQuery.data, hasIndividualsValidationWarnings)}
              onClick={onDelete}
            />
          </Space>
        ),
      },
    ],
    [hasIndividualsValidationWarnings, applicationQuery.data, t, errorRowsKeys],
  );

  const individualDataSource = getAllIndividualsData(
    shareholdersFormState,
    onChange,
    openEdit,
    refetchContacts,
    sourceOfEditing,
    contactMutation,
    allContacts,
  );

  return (
    <>
      <IndividualEdit
        uid={editId}
        onClose={closeEdit}
        setSourceOfEditing={setSourceOfEditing}
        refetchContacts={refetchContacts}
        {...props}
      />
      <Space size={24} direction="vertical">
        {!isEmpty(individualDataSource) && (
          <>
            {!isTablet ? (
              <Table
                id="individualsTable"
                columns={individualColumns}
                dataSource={individualDataSource}
                loading={applicationQuery.isLoading}
                pagination={pagination}
                rowKey="uid"
              />
            ) : (
              <Space size={12} direction="vertical">
                {individualDataSource.map((item) => (
                  <IndividualCardMobile
                    key={item.uid}
                    disableRowActions={isShareholderEditButtonDisabled(
                      applicationQuery.data,
                      hasIndividualsValidationWarnings,
                    )}
                    hasIndividualsValidationWarnings={hasIndividualsValidationWarnings}
                    {...item}
                  />
                ))}
              </Space>
            )}
          </>
        )}
        <IndividualAdd setSourceOfEditing={setSourceOfEditing} {...props} />
      </Space>
    </>
  );
};

const pagination = { hideOnSinglePage: true };

export type IndividualRowType = {
  uid: string;
  firstName: string | undefined;
  lastName: string | undefined;
  hasPassport: boolean;
  hasEmiratesId: boolean;
  shares: number | undefined;
  contactId?: string;
  onDelete?: (value: any) => void;
  onEdit?: () => void;
};

const getAllIndividualsData = (
  data: Draft<'shareholders'> | undefined,
  onFormChange: (value: any) => void,
  openEdit: (uid: string) => void,
  refetchContacts: any,
  sourceOfEditing: string,
  contactMutation: UseMutationResult<any, unknown, any, void>,
  allContacts?: any,
): IndividualRowType[] => {
  const dataSource = sourceOfEditing === 'shareholder' ? refetchContacts : allContacts;

  const updatedData: any = getUpdatedShareholderDataSync(
    { existedData: { individuals: data?.shareholders?.individuals } },
    dataSource?.data,
  );

  const individuals: IndividualRowType[] =
    updatedData?.individuals?.map((item: any) => {
      return {
        uid: item?.data?.uid as string,
        firstName: item?.data?.firstName,
        lastName: item?.data?.lastName,
        hasPassport: !isEmpty(item?.data?.passport?.files),
        hasEmiratesId: !isEmpty(item?.data?.emiratesId?.files),
        shares: item?.shares,
        onDelete: async () => {
          const updatedContact = await getUpdatedContact(item.data);
          const toDeleteIndividual = contactObjCreation(updatedContact, {
            type: 'DELETE',
            role: 'individualShareholders',
          });
          contactMutation?.mutate(toDeleteIndividual);
          onFormChange(
            produce(data, (draft: any) => {
              draft.shareholders!.individuals = draft.shareholders!.individuals!.filter(
                (a: any) => a?.data?.uid !== item?.data?.uid,
              );
            })?.shareholders as Shareholders,
          );
        },
        onEdit: () => item?.data?.uid && openEdit(item.data.uid),
      };
    }) ?? [];
  return individuals;
};

const actionsStyle = { display: 'flex', justifyContent: 'flex-end' };

export default IndividualTable;
